import {useEffect, useRef} from 'react';

interface Props {
  /** Current toggle (open/closed) state from the surrounding `WithToggle`. */
  on: boolean;
  /** Consumer callback fired whenever `on` changes. */
  onToggleCallback: (on: boolean) => void;
}

/**
 * Notifies a consumer of `WithToggle` open/closed state changes from a commit
 * phase effect instead of during render.
 *
 * `WithToggle` exposes its state through a render-prop, so historically
 * components like `Dropdown` and `Menu` invoked `onToggleCallback(on)` inline
 * while rendering that render-prop child. When the callback is a parent's
 * `setState` (a very common usage), that performs a render-phase update of a
 * different component, which React 18 surfaces as "Cannot update a component
 * while rendering a different component" and — because the update re-runs the
 * render-prop, which calls the callback again — escalates into an infinite
 * "Maximum update depth exceeded" loop.
 *
 * Rendering this component (which emits nothing) inside the render-prop defers
 * the notification to an effect and only fires on actual state changes.
 */
export default function ToggleStateNotifier({on, onToggleCallback}: Props): null {
  // Keep the latest callback without making it an effect dependency, so an
  // unstable (inline) callback doesn't re-fire the effect on every render.
  const callbackRef = useRef(onToggleCallback);
  useEffect(() => {
    callbackRef.current = onToggleCallback;
  });

  useEffect(() => {
    callbackRef.current(on);
  }, [on]);

  return null;
}
